import React, { useState } from "react";
import DemoDialog from "@/components/DemoDialog";
import { Search, ArrowRight, Play, Menu, X, ChevronDown, Car, Wrench, Laptop, BarChart3, Eye, Gauge, ShieldCheck, Leaf, CheckCircle2 } from "lucide-react";

const RED = "#c00000";
const O = { fontFamily: "Outfit, sans-serif" };
const VAN = "https://images.unsplash.com/photo-1688054005456-466170816196?w=400&q=80";
const EXCAVATOR = "https://images.unsplash.com/photo-1780417944090-5f66a20e3390?w=400&q=80";
const LAPTOPIMG = "https://images.unsplash.com/photo-1633118599122-601a3ebc8699?w=400&q=80";

const NAV = ["Plattform", "Lösungen", "Branchen", "Preise", "Ressourcen", "Unternehmen"];

function LogoC({ dark, tagline }) {
  return (
    <span className="flex items-center gap-2">
      <svg viewBox="0 0 32 32" className="w-8 h-8" aria-hidden>
        <path d="M5 27 L16 5 L20 5 L11 27 Z" fill={RED} />
        <path d="M14 27 L24 8 L28 8 L20 27 Z" fill={RED} opacity="0.65" />
      </svg>
      <span className="leading-tight">
        <span className={`block text-xl font-extrabold tracking-tight ${dark ? "text-white" : "text-gray-900"}`} style={O}>Fleet<span style={{ color: RED }}>Area</span></span>
        {tagline && <span className={`block text-[9px] font-medium ${dark ? "text-gray-400" : "text-gray-400"}`}>Alle Assets. Eine Plattform.</span>}
      </span>
    </span>
  );
}

export default function EntwurfC() {
  const [demo, setDemo] = useState(false);
  const [open, setOpen] = useState(false);
  const openDemo = () => setDemo(true);

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <a href="#" data-testid="c-logo"><LogoC tagline /></a>
          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-gray-600">
            {NAV.map((n) => <a key={n} href="#" className="hover:text-gray-900 transition-colors">{n}</a>)}
          </nav>
          <div className="hidden lg:flex items-center gap-4">
            <button aria-label="Suche" className="text-gray-500 hover:text-gray-900 transition-colors"><Search className="w-5 h-5" /></button>
            <button className="flex items-center gap-1 text-sm font-medium text-gray-600">DE <ChevronDown className="w-3.5 h-3.5" /></button>
            <button data-testid="c-cta-header" onClick={openDemo} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white rounded-lg px-4 py-2 hover:brightness-110 transition-all" style={{ background: RED }}>Jetzt kontaktieren <ArrowRight className="w-4 h-4" /></button>
          </div>
          <button className="lg:hidden text-gray-700" onClick={() => setOpen((v) => !v)} data-testid="c-mobile-toggle">{open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-gray-200 px-4 py-4 space-y-1">
            {NAV.map((n) => <a key={n} href="#" onClick={() => setOpen(false)} className="block py-2 text-gray-700">{n}</a>)}
            <button onClick={() => { setOpen(false); openDemo(); }} className="w-full mt-2 py-2.5 rounded-lg text-white text-sm font-semibold" style={{ background: RED }}>Jetzt kontaktieren</button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold tracking-[0.16em] mb-4" style={{ color: RED }}>FLEET & ASSET MANAGEMENT FÜR IHRE GESAMTE ORGANISATION</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-gray-900" style={O}>
              Verwalten Sie Ihre <span style={{ color: RED }}>gesamte Flotte</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed">Eine Plattform für Fahrzeuge, Maschinen und IT-Geräte – transparent, effizient und zukunftssicher.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button data-testid="c-hero-cta" onClick={openDemo} className="inline-flex items-center justify-center gap-2 text-white font-bold rounded-xl px-6 py-3.5 text-sm hover:brightness-110 transition-all shadow-lg" style={{ background: RED, boxShadow: `0 12px 34px -14px ${RED}` }}>Jetzt unverbindlich anfragen <ArrowRight className="w-4 h-4" /></button>
              <button onClick={openDemo} className="inline-flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-xl px-6 py-3.5 text-sm transition-colors"><Play className="w-4 h-4" style={{ color: RED }} /> Plattform entdecken</button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs sm:text-sm text-gray-600">
              {["Alle Assets in einer Plattform", "Schnell einsatzbereit", "Bewährt in Unternehmen"].map((x) => (
                <span key={x} className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: RED }} /> {x}</span>
              ))}
            </div>
          </div>
          <NetworkHero />
        </div>
      </section>

      {/* TRUST (neutral) */}
      <section className="py-6 px-4 sm:px-6 border-y border-gray-100 bg-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center gap-4 justify-between">
          <span className="text-sm text-gray-500 font-medium shrink-0">Eingesetzt in verschiedenen Branchen.</span>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {["Logistik", "Bauwirtschaft", "Fertigung", "Gesundheit", "Handel", "Und viele mehr"].map((b) => (
              <span key={b} className="text-sm font-bold text-gray-400 tracking-tight" style={O}>{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* MODULE */}
      <section className="py-16 px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" style={O}>Eine Plattform. Alle Asset-Klassen.</h2>
            <p className="mt-3 text-gray-600 max-w-2xl">FleetArea vereint Fahrzeugmanagement, Maschinenmanagement und IT-Asset-Management in einer modularen Plattform – für maximale Transparenz und Effizienz.</p>
          </div>
          <a href="#" className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold shrink-0" style={{ color: RED }}>Alle Module im Überblick <ArrowRight className="w-4 h-4" /></a>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[[Car, "Fahrzeugmanagement", "Pkw, Lkw, Transporter und Spezialfahrzeuge – vom Leasing bis zur Verfügbarkeit."], [Wrench, "Maschinenmanagement", "Baumaschinen, Produktionsanlagen und Spezialtechnik – einsatzbereit und wirtschaftlich im Blick."], [Laptop, "IT-Asset-Management", "Laptops, Smartphones, Tablets und Peripherie – sicher, compliant und effizient."], [BarChart3, "Reporting & Analytics", "Alle Daten in aussagekräftigen Dashboards – für bessere Entscheidungen."]].map(([Icon, t, d], i) => (
            <div key={i} data-testid={`c-modul-${i}`} className="group bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all">
              <span className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: `${RED}12` }}><Icon className="w-6 h-6" style={{ color: RED }} /></span>
              <h3 className="text-base font-bold text-gray-900 mb-1.5" style={O}>{t}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{d}</p>
              <ArrowRight className="w-4 h-4 mt-4 transition-transform group-hover:translate-x-1" style={{ color: RED }} />
            </div>
          ))}
        </div>
      </section>

      {/* EINBLICKE */}
      <section className="py-16 px-4 sm:px-6 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" style={O}>Einblicke in die Plattform</h2>
              <p className="mt-3 text-gray-600">Übersichtliche Dashboards, transparente Daten und alle relevanten Informationen auf einen Blick.</p>
            </div>
            <a href="#" className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold shrink-0" style={{ color: RED }}>Live-Demo vereinbaren <ArrowRight className="w-4 h-4" /></a>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[["Fahrzeuge", [["452", "gesamt"], ["37", "in Wartung"], ["12", "außer Betrieb"], ["98%", "Verfügbar"]], ["B-FA 2043 · Mercedes Sprinter", "HH-FA 7712 · BMW 320d", "K-FA 9901 · MAN TGS"]], ["Maschinen", [["128", "gesamt"], ["14", "im Einsatz"], ["8", "in Wartung"], ["96%", "Verfügbar"]], ["Liebherr R 920 · Bagger", "Wacker Neuson · Radlader", "Bomag BW 174 · Walze"]], ["IT-Geräte", [["1.248", "gesamt"], ["103", "in Ausgabe"], ["27", "in Reparatur"], ["99%", "Compliance"]], ["Dell Latitude 7540 · Laptop", "iPhone 15 · Smartphone", "iPad Air · Tablet"]]].map(([title, stats, rows], i) => (
              <div key={i} data-testid={`c-insight-${i}`} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                  <LogoC />
                  <span className="ml-auto text-[9px] font-mono text-gray-400">Demodaten</span>
                </div>
                <div className="p-3">
                  <div className="text-sm font-bold text-gray-900 mb-2" style={O}>{title}</div>
                  <div className="grid grid-cols-4 gap-1.5 mb-3">
                    {stats.map(([v, l], j) => (
                      <div key={j} className="text-center rounded-md bg-gray-50 border border-gray-100 py-1.5">
                        <div className="text-sm font-extrabold" style={{ ...O, color: j === 2 ? RED : "#111827" }}>{v}</div>
                        <div className="text-[8px] text-gray-400">{l}</div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1">
                    {rows.map((r, k) => (
                      <div key={k} className="flex items-center gap-2 text-[10px] py-1 border-b border-gray-50 last:border-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span className="text-gray-700 truncate">{r}</span>
                        <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 font-semibold shrink-0">Aktiv</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VORTEILE + CTA */}
      <section className="py-16 px-4 sm:px-6 max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-8" style={O}>Ihre Vorteile mit FleetArea</h2>
        <div className="grid lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {[[Eye, "Volle Transparenz", "Alle Assets, Standorte und Kosten – in einer Plattform."], [Gauge, "Höhere Effizienz", "Weniger Aufwand, automatisierte Prozesse, mehr Produktivität."], [ShieldCheck, "Mehr Compliance", "Revisionssicher, DSGVO-konform und auditfähig."], [Leaf, "Nachhaltige Wertschöpfung", "Ressourcen optimal nutzen und CO₂-Emissionen reduzieren."]].map(([Icon, t, d], i) => (
              <div key={i} className="flex gap-3">
                <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${RED}12` }}><Icon className="w-5 h-5" style={{ color: RED }} /></span>
                <div><div className="font-bold text-gray-900" style={O}>{t}</div><p className="text-sm text-gray-600 mt-0.5 leading-relaxed">{d}</p></div>
              </div>
            ))}
          </div>
          <div className="rounded-2xl p-7 text-white flex flex-col justify-center" style={{ background: RED }}>
            <h3 className="text-xl font-bold" style={O}>Bereit für den nächsten Schritt?</h3>
            <p className="text-sm text-white/85 mt-2">Erfahren Sie, wie FleetArea auch in Ihrem Unternehmen Mehrwert schafft.</p>
            <button data-testid="c-cta-card" onClick={openDemo} className="mt-5 inline-flex items-center justify-center gap-2 bg-white font-bold rounded-xl px-4 py-3 text-sm hover:bg-gray-100 transition-colors" style={{ color: RED }}>Jetzt kontaktieren <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="text-gray-400 px-4 sm:px-6 py-12" style={{ background: "#1B1E23" }}>
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row justify-between gap-8">
          <div><LogoC dark tagline /></div>
          <nav className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            {["Plattform", "Lösungen", "Branchen", "Ressourcen", "Unternehmen", "Karriere"].map((x) => <a key={x} href="#" className="hover:text-white transition-colors">{x}</a>)}
          </nav>
          <div className="flex items-center gap-5 text-sm">
            <span className="flex items-center gap-1">DE <ChevronDown className="w-3.5 h-3.5" /></span>
            <a href="#" className="hover:text-white transition-colors">Datenschutz</a>
            <a href="#" className="hover:text-white transition-colors">Impressum</a>
          </div>
        </div>
      </footer>

      <DemoDialog open={demo} onClose={() => setDemo(false)} />
    </div>
  );
}

function NetworkHero() {
  const sat = [
    { img: VAN, label: "Fahrzeuge", sub: "Pkw, Lkw, Transporter", pos: "top-0 left-4", cx: 130, cy: 60 },
    { img: EXCAVATOR, label: "Maschinen", sub: "Bau, Produktion", pos: "bottom-2 right-2", cx: 340, cy: 250 },
    { img: LAPTOPIMG, label: "IT-Geräte", sub: "Laptops, Peripherie", pos: "bottom-6 left-0", cx: 90, cy: 260 },
  ];
  return (
    <div className="relative h-[340px] hidden lg:block" data-testid="c-network">
      <svg viewBox="0 0 420 340" className="absolute inset-0 w-full h-full" aria-hidden>
        <defs><radialGradient id="dot"><stop offset="0%" stopColor={RED} stopOpacity="0.25" /><stop offset="100%" stopColor={RED} stopOpacity="0" /></radialGradient></defs>
        {sat.map((s, i) => <line key={i} x1="230" y1="170" x2={s.cx} y2={s.cy} stroke={RED} strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5" />)}
        {[...Array(24)].map((_, i) => <circle key={i} cx={40 + (i * 63) % 380} cy={30 + ((i * 97) % 300)} r="2" fill={RED} opacity="0.18" />)}
      </svg>
      {/* zentraler Hub */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full flex flex-col items-center justify-center text-white text-center shadow-2xl" style={{ background: RED }}>
        <span className="text-sm font-extrabold" style={O}>FleetArea</span>
        <span className="text-[9px] text-white/80 mt-0.5 px-2">Alle Assets.<br />Eine Plattform.</span>
      </div>
      {/* Satelliten */}
      {sat.map((s) => (
        <div key={s.label} className={`absolute ${s.pos} flex items-center gap-2`}>
          <span className="w-16 h-16 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white shrink-0"><img src={s.img} alt={s.label} className="w-full h-full object-cover" loading="lazy" /></span>
          <div className="bg-white/90 backdrop-blur rounded-lg px-2 py-1 shadow-sm"><div className="text-xs font-bold text-gray-900" style={O}>{s.label}</div><div className="text-[9px] text-gray-500">{s.sub}</div></div>
        </div>
      ))}
      <div className="absolute top-2 right-0 text-right text-[9px] font-bold tracking-widest text-gray-400 leading-relaxed" style={O}>TRANSPARENZ<br />EFFIZIENZ<br />COMPLIANCE<br />NACHHALTIGKEIT</div>
    </div>
  );
}
